/**
 * Stappenindicator: óf legacy (leeg/tekening-flow), óf uitlijning met 4 dossier-tabs.
 */

import { NavLink } from 'react-router-dom';
import { BarChart3, FileCheck2, FolderKanban, Layers2, ScanLine, Upload } from 'lucide-react';
import type { JourneyVariant } from '../lib/project-flow-copy';
import {
  getFollowingStepSummary,
  getFollowingTabStepSummary,
  JOURNEY_STEPS_DRAWING,
  JOURNEY_STEPS_EMPTY,
  JOURNEY_TAB_STEPS,
} from '../lib/project-flow-copy';

/** Alleen voor nieuw-dossier wizard — niet in dossier-zijbalk (voorkomt dubbele navigatie). */
export function ProjectFlowVisual({
  variant,
  tagline,
  className = '',
}: {
  variant: JourneyVariant;
  tagline?: string;
  className?: string;
}) {
  return (
    <div className={className}>
      <FlowVisualHero variant={variant} compact={false} />
      {tagline ? (
        <p className="-mt-1 mb-0 text-center text-xs text-text-muted">{tagline}</p>
      ) : null}
    </div>
  );
}

function FlowVisualHero({ variant, compact }: { variant: JourneyVariant; compact?: boolean }) {
  const emptyIcons = [
    { Icon: FolderKanban, label: 'Dossier' },
    { Icon: Layers2, label: 'Posten' },
    { Icon: BarChart3, label: 'Totalen' },
  ] as const;
  const drawingIcons = [
    { Icon: Upload, label: 'Upload' },
    { Icon: ScanLine, label: 'Analyse' },
    { Icon: FileCheck2, label: 'Review' },
  ] as const;
  const items = variant === 'empty' ? emptyIcons : drawingIcons;

  const box = compact ? 'h-10 w-10 rounded-xl' : 'h-14 w-14 rounded-2xl';
  const iconSz = compact ? 'h-5 w-5' : 'h-7 w-7';
  const wCol = compact ? 'w-[4.5rem]' : 'w-[5.25rem] sm:w-[6rem]';
  const mbArrow = compact ? 'mb-4' : 'mb-7';

  return (
    <div
      className={`${compact ? 'mb-2 border-b border-border/60 pb-2' : 'mb-4 border-b border-border/80 pb-4'}`}
      aria-hidden
    >
      <ol className="mx-auto flex max-w-xl flex-wrap items-start justify-center gap-1 sm:flex-nowrap sm:items-center sm:gap-0.5">
        {items.map(({ Icon, label }, idx) => (
          <li key={label} className="flex items-center">
            <div className={`flex ${wCol} flex-col items-center gap-1`}>
              <div
                className={`flex ${box} items-center justify-center bg-primary/12 text-primary shadow-sm ring-1 ring-primary/20`}
              >
                <Icon className={iconSz} strokeWidth={1.65} />
              </div>
              <span
                className={`text-center font-semibold uppercase leading-tight tracking-wide text-text-muted ${
                  compact ? 'text-[9px]' : 'text-[11px]'
                }`}
              >
                {label}
              </span>
            </div>
            {idx < items.length - 1 && (
              <span className={`mx-0.5 ${mbArrow} hidden text-sm font-light text-primary/45 sm:inline`} aria-hidden>
                →
              </span>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

export function ProjectJourneySteps({
  variant,
  currentStep,
  profileHint,
  className = '',
  compact = false,
  /** true = alleen 4 tabs (Gegevens→Rapportage), geen 3-iconenrij */
  alignWithTabs = false,
  /** Zelfde routes als dossier-tabs — stappen worden klikbaar (alleen met alignWithTabs). */
  tabStepLinks,
}: {
  variant: JourneyVariant;
  /** 1-based actieve stap (bij alignWithTabs: 1=Gegevens … 4=Rapportage) */
  currentStep: number;
  profileHint?: string;
  className?: string;
  compact?: boolean;
  alignWithTabs?: boolean;
  tabStepLinks?: Array<{ to: string; end?: boolean; disabled?: boolean }>;
}) {
  const steps = alignWithTabs
    ? JOURNEY_TAB_STEPS
    : variant === 'empty'
      ? JOURNEY_STEPS_EMPTY
      : JOURNEY_STEPS_DRAWING;
  const nextLine = alignWithTabs
    ? getFollowingTabStepSummary(currentStep)
    : getFollowingStepSummary(variant, currentStep);

  return (
    <div
      className={`rounded-xl border border-border bg-surface-card/80 shadow-sm ${
        compact ? 'p-3' : 'p-4'
      } ${className}`}
    >
      <p
        className={`mb-2 font-semibold uppercase tracking-wide text-text-muted ${compact ? 'text-[10px]' : 'text-xs'}`}
      >
        {alignWithTabs ? 'Je route (4 stappen)' : 'Jouw route'}
      </p>
      {alignWithTabs && (
        <p className={`mb-3 text-text-muted ${compact ? 'text-[10px] leading-snug' : 'text-xs'}`}>
          Zelfde route als de <strong className="text-text">tabs</strong>.{' '}
          <strong className="text-text">Klik een stap</strong> om direct te wisselen — ook als je ver naar beneden
          bent gescrolld (tabs blijven bovenaan plakken).
        </p>
      )}
      <ol className={`grid list-none gap-2 p-0 ${compact ? '' : 'sm:grid-cols-2'}`}>
        {steps.map((s, i) => {
          const n = i + 1;
          const done = n < currentStep;
          const active = n === currentStep;
          const link = tabStepLinks?.[i];
          const useLinks = Boolean(alignWithTabs && link && tabStepLinks!.length === steps.length);

          const pad = compact ? 'px-2 py-1.5 text-xs' : 'px-3 py-2.5 text-sm';
          const stateCls = active
            ? 'border-primary bg-primary/10 ring-2 ring-primary/35'
            : done
              ? 'border-border/80 bg-surface text-text-muted'
              : 'border-border/60 bg-surface text-text-muted/90';

          const inner = (
            <>
              <span className="mb-0.5 flex items-center gap-2 font-semibold text-text">
                <span
                  className={`flex shrink-0 items-center justify-center rounded-full font-bold ${
                    compact ? 'h-5 w-5 text-[10px]' : 'h-6 w-6 text-xs'
                  } ${
                    done
                      ? 'bg-primary text-white'
                      : active
                        ? 'bg-primary text-white'
                        : 'bg-border text-text-muted'
                  }`}
                  aria-hidden
                >
                  {done ? '✓' : n}
                </span>
                <span className={compact ? 'text-xs leading-tight' : ''}>{s.title}</span>
              </span>
              <p
                className={`mt-0.5 pl-7 leading-snug text-text-muted ${compact ? 'text-[10px]' : 'mt-1 pl-8 text-xs'}`}
              >
                {s.blurb}
              </p>
            </>
          );

          if (useLinks && link!.disabled) {
            return (
              <li
                key={s.key}
                className={`relative rounded-lg border transition ${pad} ${stateCls} cursor-not-allowed opacity-60`}
                title="Sla minstens één begrotingsregel op bij Posten om Resultaat en Rapportage te openen."
              >
                {inner}
              </li>
            );
          }

          if (useLinks && link && !link.disabled) {
            return (
              <li key={s.key} className="m-0 list-none p-0">
                <NavLink
                  to={link.to}
                  end={link.end}
                  className={`relative block rounded-lg border text-left no-underline transition ${pad} ${stateCls} ${
                    !active ? 'hover:border-primary/40 hover:bg-surface-card' : ''
                  }`}
                >
                  {inner}
                </NavLink>
              </li>
            );
          }

          return (
            <li
              key={s.key}
              className={`relative rounded-lg border transition ${pad} ${stateCls}`}
            >
              {inner}
            </li>
          );
        })}
      </ol>
      {profileHint && (
        <p
          className={`mt-3 rounded-lg bg-surface px-2 py-1.5 leading-relaxed text-text-muted ${
            compact ? 'text-[10px]' : 'mt-4 px-3 py-2 text-xs'
          }`}
        >
          <span className="font-medium text-text">Profiel: </span>
          {profileHint}
        </p>
      )}
      {nextLine && (
        <p
          className={`mt-2 border-t border-border pt-2 font-medium text-primary ${
            compact ? 'text-[10px]' : 'mt-3 pt-3 text-xs'
          }`}
        >
          {nextLine}
        </p>
      )}
    </div>
  );
}
